<script setup>
import ViewEmailingStatisticsAction from '@frontJs/components/actions/projectManagement/modules/ViewEmailingStatisticsAction.vue';
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import EyeIcon from '@iconify-vue/ph/eye';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PercentageCell from '@js/components/tables/cells/PercentageCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, all = false } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    all: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'tenant.name',
            headerCustom: true,
            enableSorting: false,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.tenant?.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date',
                }),
            cell: (props) =>
                props.row.original.date
                    ? h(TextCell, {
                          text: props.row.original.date,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'isActive',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actif',
                }),
            cell: (props) =>
                props.row.original.isActive !== undefined
                    ? h(StatusCell, {
                          status: props.row.original.isActive,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'emails',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB emails<br/>envoyés',
                }),
            cell: (props) =>
                props.row.original.emails !== undefined
                    ? h(NumberCell, {
                          number: props.row.original.emails,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'opened',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Ouvertures',
                }),
            cell: (props) =>
                props.row.original.opened !== undefined
                    ? h(PercentageCell, {
                          percentage: props.row.original.opened,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'clicked',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Clics',
                }),
            cell: (props) =>
                props.row.original.clicked !== undefined
                    ? h(PercentageCell, {
                          percentage: props.row.original.clicked,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfOrders',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'NB<br/>cdes',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfOrders,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'revenue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA<br/>TTC',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.revenue,
                    withDecimals: false,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'unsub',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Désabonnements',
                }),
            cell: (props) =>
                props.row.original.unsub !== undefined
                    ? h(PercentageCell, {
                          percentage: props.row.original.unsub,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) => {
                const rowTenant = tenant ?? props.row.original.tenant;

                return h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            ...(props.row.depth === 0
                                ? [
                                      h(
                                          ViewEmailingStatisticsAction,
                                          {
                                              tenant: rowTenant,
                                              emailing: props.row.original,
                                          },
                                          {
                                              trigger: () =>
                                                  h(ActionButton, {
                                                      size: 'small',
                                                      type: 'primary',
                                                      title: 'Voir les statistiques',
                                                      icon: EyeIcon,
                                                      square: true,
                                                  }),
                                          },
                                      ),
                                      h(ActionButton, {
                                          size: 'small',
                                          title: props.row.getIsExpanded()
                                              ? 'Réduire'
                                              : 'Développer',
                                          icon: CaretDownIcon,
                                          square: true,
                                          rotateIcon: props.row.getIsExpanded(),
                                          callback: () =>
                                              props.row.toggleExpanded(),
                                      }),
                                  ]
                                : []),
                        ],
                    },
                );
            },
        }),
    ],
    url: route(
        'front.projectManagement.modules.postEmailingGeneralStatistics',
        {
            tenant: tenant ? tenant.id : null,
            all,
        },
    ),
    columnPinning: {
        right: ['details'],
    },
    columnVisibility: {
        'tenant.name': !tenant,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
